import type { SoaLineItemsMeta } from "@/components/tables/types";

export interface SoaApiColumn {
  id: number | null;
  name: string;
}

export interface SoaDisplayColumn {
  id: number;
  /** Exact column name from the backend (display + row key). */
  name: string;
  /** Row object key — same as backend column name. */
  dataKey: string;
}

const normalizeColumnKey = (value: string): string =>
  value.toLowerCase().trim();

const isValidApiColumn = (column: unknown): column is SoaApiColumn => {
  if (column == null || typeof column !== "object") return false;
  const name = (column as SoaApiColumn).name;
  return typeof name === "string" && name.trim() !== "";
};

/**
 * Pass through API columns as-is: same names, same order, no remapping.
 */
export function buildSoaDisplayColumns(
  apiColumns: SoaApiColumn[],
): SoaDisplayColumn[] {
  if (!Array.isArray(apiColumns)) return [];

  return apiColumns.filter(isValidApiColumn).map((column, index) => ({
    // Backend may return null id for injected columns (e.g. Booking Number).
    id: typeof column.id === "number" ? column.id : -(index + 1),
    name: column.name,
    dataKey: column.name,
  }));
}

export const isSoaNumericColumn = (header: string): boolean => {
  const lower = normalizeColumnKey(String(header ?? ""));
  return (
    lower === "amount" ||
    lower === "total amount" ||
    lower.includes("vat") ||
    lower === "stack run"
  );
};

/** Map a display header to the corresponding all-pages total from API meta. */
export const getSoaFooterTotalForColumn = (
  header: string,
  meta: SoaLineItemsMeta | null,
): number | null => {
  if (!meta) return null;
  const lower = normalizeColumnKey(String(header ?? ""));
  if (lower.includes("vat")) {
    return meta.total_vat ?? null;
  }
  if (lower === "amount") {
    return meta.total_amount ?? null;
  }
  return null;
};

const parseNumericCellValue = (value: unknown): number => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : Number.NaN;
  }
  if (typeof value === "string" && value !== "") {
    return Number.parseFloat(value.replace(/,/g, ""));
  }
  return Number.NaN;
};

/** Fallback: sum AMOUNT column across a full in-memory dataset (client-side pagination). */
export const sumSoaAmountColumn = (
  rows: Record<string, unknown>[],
  columns: SoaDisplayColumn[],
): number => {
  const amountColumn = columns.find(
    (column) => normalizeColumnKey(column.name) === "amount",
  );
  if (!amountColumn) return 0;

  return rows.reduce((sum, row) => {
    const num = parseNumericCellValue(row[amountColumn.dataKey]);
    return Number.isFinite(num) ? sum + num : sum;
  }, 0);
};

export const getSoaCellValue = (
  row: Record<string, unknown>,
  column: SoaDisplayColumn,
): unknown => row[column.dataKey];

/** Stable React key — prefer dataKey (unique) over id (can be null/synthetic). */
export const getSoaColumnKey = (
  column: SoaDisplayColumn,
  index: number,
): string => `${column.dataKey || column.name || "col"}-${index}`;
